import { jsx, jsxs } from "react/jsx-runtime";
import React__default from "react";
import classNames from "classnames";
import { AnalyticsId, IconSize } from "../../constants.js";
import { useUuid } from "../../hooks/useUuid.js";
import { getColor } from "../../theme/currys/color.js";
import { getAriaLabelAttributes } from "../../utils/accessibility.js";
import { N as NotificationBadge } from "../../NotificationBadge.js";
import { C as Close } from "../../Close.js";
import { E as Expander } from "../../Expander.js";
import styles from "./styles.module.scss";
const FluidWrapper = ({ fluid, children }) => {
  if (fluid) {
    const fluidClasses = classNames(styles["fluid-wrapper"]);
    return /* @__PURE__ */ jsx("div", { className: fluidClasses, children });
  }
  return children;
};
const NotificationPanel = React__default.forwardRef((props, ref) => {
  const {
    children,
    variant = "info",
    dismissable = false,
    onClick,
    expanderChildren,
    expanderButtonText,
    expanderButtonClosedText,
    expanderOpenFromStart = false,
    compactVariant,
    label,
    labelHtmlMarkup = "h1",
    fluid = false,
    size,
    className,
    labelId,
    role,
    testId
  } = props;
  const uuid = useUuid(labelId);
  const renderContent = () => {
    const outlineVariant = compactVariant === "outline";
    const contentClasses = classNames(styles["notification-panel__content"]);
    const labelClasses = classNames(styles["notification-panel__label"], {
      [styles["notification-panel__label--no-content"]]: !children && !expanderChildren,
      [styles["notification-panel__label--compact"]]: !!compactVariant,
      [styles["notification-panel__label--outline"]]: outlineVariant
    });
    const childrenClasses = classNames(styles["notification-panel__children"], {
      [styles["notification-panel__children--with-label"]]: label,
      [styles["notification-panel__children--expander-no-label"]]: expanderChildren && !label,
      [styles["notification-panel__children--outline"]]: outlineVariant
    });
    const CustomTag = labelHtmlMarkup;
    const [expanderOpen, setExpanderOpen] = React__default.useState(expanderOpenFromStart);
    return /* @__PURE__ */ jsxs("div", { className: contentClasses, id: !label ? uuid : void 0, children: [
      label && /* @__PURE__ */ jsx(CustomTag, { className: labelClasses, id: uuid, children: label }),
      children && /* @__PURE__ */ jsx("div", { className: childrenClasses, children }),
      expanderChildren && expanderButtonText && expanderButtonClosedText && !compactVariant && /* @__PURE__ */ jsx(
        Expander,
        {
          title: expanderOpen ? expanderButtonText : expanderButtonClosedText,
          onExpand: setExpanderOpen,
          expanded: expanderOpen,
          testId: "expand",
          children: expanderChildren
        }
      )
    ] });
  };
  const notificationPanelClasses = classNames(
    styles["notification-panel"],
    styles[`notification-panel--${variant}`],
    size && styles[`notification-panel--${size}`],
    {
      [styles["notification-panel--compact"]]: !!compactVariant,
      [styles["notification-panel--compact--basic"]]: compactVariant === "basic",
      [styles["notification-panel--compact--outline"]]: compactVariant === "outline",
      [styles["notification-panel--with-content"]]: expanderChildren || label && children,
      [styles["notification-panel--dismissable"]]: dismissable
    },
    className
  );
  const ariaRole = role || variant === "error" && "alert" || void 0;
  const ariaLabelAttributes = ariaRole ? getAriaLabelAttributes({ label, id: uuid }) : void 0;
  return /* @__PURE__ */ jsx(FluidWrapper, { fluid, children: /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      role: ariaRole,
      "data-testid": testId,
      "data-analyticsid": AnalyticsId.NotificationPanel,
      className: notificationPanelClasses,
      ...ariaLabelAttributes,
      children: [
        /* @__PURE__ */ jsx(
          NotificationBadge,
          {
            variant,
            size: compactVariant ? IconSize.XSmall : IconSize.Small,
            className: styles["notification-panel__icon"]
          }
        ),
        dismissable && /* @__PURE__ */ jsx("span", { className: styles["notification-panel__close"], children: /* @__PURE__ */ jsx(Close, { ariaLabel: props.ariaLabelCloseBtn, onClick, color: getColor("black") }) }),
        renderContent()
      ]
    }
  ) });
});
export {
  NotificationPanel as default
};
//# sourceMappingURL=index.js.map
